import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import BlogContent from '../components/BlogContent';
import { formatDate } from '../utils/blogUtils';

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readTime, setReadTime] = useState(5);

  useEffect(() => {
    // Scroll to top when component mounts or slug changes
    window.scrollTo(0, 0);

    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/data/blog-posts/${slug}.json`);

        if (!response.ok) {
          throw new Error('Blog post not found');
        }

        const data = await response.json();
        setPost(data);

        // Calculate read time from content
        const text = data.content
          .filter((block) => block.type === 'paragraph' || block.type === 'heading')
          .map((block) => block.text)
          .join(' ');

        // Simple word count calculation (250 words per minute)
        const words = text.split(/\s+/).length;
        const minutes = Math.ceil(words / 250) || 1;
        setReadTime(minutes);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4" />
          <p className="text-gray-400">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">Blog post not found</p>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/blog"
            className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors"
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/blog"
            className="text-cyan-400 hover:text-cyan-300 mb-8 inline-flex items-center transition-colors"
          >
            ← Back to Blog
          </Link>

          {/* Featured Image */}
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-96 object-cover rounded-lg mb-8"
          />

          {/* Title */}
          <h1 className="text-4xl font-bold text-cyan-400 mb-4">
            {post.title}
          </h1>

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-8 pb-8 border-b border-gray-700">
            <span>{formatDate(post.date)}</span>
            <span>•</span>
            <span>
              {readTime}
              {' '}
              min read
            </span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="bg-gray-700 text-cyan-400 px-3 py-1 rounded-full text-sm border border-cyan-400/30"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Content */}
          <BlogContent content={post.content} />

          {/* Back to Blog Footer */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <Link
              to="/blog"
              className="text-cyan-400 hover:text-cyan-300 font-semibold inline-flex items-center transition-colors"
            >
              ← Back to Blog
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}

export default BlogPost;
